/**
 * Relative-time rules for web post surfaces (feed, post detail, media viewer,
 * profile and group posts, comments, replies, notifications).
 *
 * The bucket is chosen from elapsed milliseconds, never from a difference of
 * UTC calendar dates, so a post stays "30 mins ago" when UTC rolls into the
 * next day. Only the Yesterday/day buckets look at calendar days, and those
 * use the viewer's local calendar, which is what "yesterday" means to them.
 */

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** Past four weeks a date is easier to read than a growing "x weeks ago". */
const MAX_RELATIVE_WEEKS = 4;

export type RelativeTimeDescriptor =
  | { kind: 'justNow' }
  | { kind: 'minutes'; value: number }
  | { kind: 'hours'; value: number }
  | { kind: 'yesterday' }
  | { kind: 'days'; value: number }
  | { kind: 'weeks'; value: number }
  | { kind: 'date' };

/** Whole days between two instants on the viewer's local calendar. */
export function localCalendarDaysBetween(from: Date, to: Date): number {
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime();
  return Math.round((end - start) / DAY_MS);
}

/**
 * Chooses the bucket for an instant. Pure and locale-free so it can be tested
 * directly; `useRelativeTime` maps the result onto translated copy.
 */
export function describeRelativeTime(
  date: Date,
  now: Date = new Date(),
): RelativeTimeDescriptor {
  // Clamped: a viewer clock slightly ahead of the server must not read as future.
  const elapsedMs = Math.max(0, now.getTime() - date.getTime());

  if (elapsedMs < MINUTE_MS) return { kind: 'justNow' };
  if (elapsedMs < HOUR_MS) {
    return { kind: 'minutes', value: Math.floor(elapsedMs / MINUTE_MS) };
  }
  if (elapsedMs < DAY_MS) {
    return { kind: 'hours', value: Math.floor(elapsedMs / HOUR_MS) };
  }

  const calendarDays = localCalendarDaysBetween(date, now);
  if (calendarDays === 1) return { kind: 'yesterday' };

  const elapsedDays = Math.floor(elapsedMs / DAY_MS);
  // Past 24h the elapsed count and the calendar count can differ by one across
  // a DST change; the calendar reading is the one the viewer recognises.
  const days = Math.max(calendarDays, elapsedDays);
  if (days < 7) return { kind: 'days', value: days };

  const weeks = Math.floor(days / 7);
  if (weeks <= MAX_RELATIVE_WEEKS) return { kind: 'weeks', value: weeks };

  return { kind: 'date' };
}

/** Localized fallback for anything older than the relative window. */
export function formatLocalizedDate(date: Date, now: Date = new Date()): string {
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString(
    undefined,
    sameYear
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' },
  );
}
